import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { CreatePreview } from './CreatePreview'

export interface PendingConfirmation {
  actionToken: string
  tool: string
  summary: string
  payload: Record<string, unknown>
  currentState: Record<string, unknown> | null
  provenance: 'user_authored' | 'tool_output'
  pendingIndex: number
  pendingTotal: number
}

interface Props {
  pending: PendingConfirmation
  isSubmitting: boolean
  onConfirm: (token: string) => void
  onReject: (token: string) => void
  /** Rejects every pending confirmation at once. Pass it only when more than one waits. */
  onRejectAll?: () => void
}

enum Decision {
  CONFIRM = 'confirm',
  REJECT = 'reject',
  REJECT_ALL = 'rejectAll',
}

/**
 * Inline confirmation — rendered as the last block in the chat when the agent
 * has a write action awaiting approval. One info Banner holds the summary, the
 * provenance warning, the payload preview and the decision buttons. Once
 * confirmed, only the summary and the buttons remain.
 */
export function ConfirmationCard({ pending, isSubmitting, onConfirm, onReject, onRejectAll }: Props) {
  const [decisionInFlight, setDecisionInFlight] = useState<Decision | null>(null)
  const confirming = decisionInFlight === Decision.CONFIRM
  // Kept while its own request runs, so the button doesn't vanish from a card on its way out.
  const showRejectAll = Boolean(onRejectAll) || decisionInFlight === Decision.REJECT_ALL

  useEffect(() => {
    setDecisionInFlight(null)
  }, [pending.actionToken])

  const handleClick = (decision: Decision) => {
    if (isSubmitting) return
    setDecisionInFlight(decision)
    if (decision === Decision.CONFIRM) onConfirm(pending.actionToken)
    else if (decision === Decision.REJECT) onReject(pending.actionToken)
    else onRejectAll?.()
  }

  const loadingFor = (decision: Decision, text: string) =>
    isSubmitting && decisionInFlight === decision ? text : undefined

  return (
    <Banner
      variant="info"
      icon={<ShieldCheck />}
      action={
        // Reverse wrap: where the three don't fit, Reject all drops below the pair.
        <div className="flex flex-wrap-reverse items-center gap-2">
          {showRejectAll && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleClick(Decision.REJECT_ALL)}
              disabled={isSubmitting}
              loading={loadingFor(Decision.REJECT_ALL, 'Rejecting…')}
            >
              Reject all
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleClick(Decision.REJECT)}
              disabled={isSubmitting}
              loading={loadingFor(Decision.REJECT, 'Rejecting…')}
            >
              Reject
            </Button>
            <Button
              size="sm"
              onClick={() => handleClick(Decision.CONFIRM)}
              disabled={isSubmitting}
              loading={loadingFor(Decision.CONFIRM, 'Confirming…')}
            >
              Confirm
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-2">
        <p className="font-semibold">
          {pending.summary}
          {pending.pendingTotal > 1 && (
            <span className="ml-2 text-xs font-normal">
              {pending.pendingIndex} of {pending.pendingTotal}
            </span>
          )}
        </p>
        <p>Confirmation required. Confirm to apply, or reject to keep things as they are.</p>
        {!confirming && pending.provenance === 'tool_output' && (
          <p className="flex items-start gap-1.5 text-(--amber-text)">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>
              This action was proposed based on content the agent read (uploaded document, KB, or search
              result). Review carefully before confirming.
            </span>
          </p>
        )}
        {!confirming && <CreatePreview payload={pending.payload} />}
      </div>
    </Banner>
  )
}
